import React from "react";
import { SegmentLatencyResponse } from "../apiResponses/realtimeResponse";
import { Box, Group, LoadingOverlay, Text } from "@mantine/core";

import latencyClasses from "../assets/styles/latencyDisplay.module.css";
import PlotIcon from "../assets/icons/PlotIcon";
import { observer } from "mobx-react-lite";
import { isArray } from "../utils/typeHelpers";

interface LatencyDisplayProps {
  // Null -> loading
  // [] -> there is no data
  data: SegmentLatencyResponse | null | [];
}

const LatencyDisplay: React.FC<LatencyDisplayProps> = observer(({ data }) => {
  let innerElement = null;
  if (isArray(data)) {
    innerElement = <Text fw={600}>No data in this time range.</Text>;
  } else if (data === null) {
    innerElement = (
      <React.Fragment>
        <Box w={200} h={30} />
        <LoadingOverlay
          visible={true}
          loaderProps={{ type: "dots", size: "lg" }}
        />
      </React.Fragment>
    );
  } else {
    innerElement = (
      <React.Fragment>
        <LatencyPlot p50={data.median} p99={data.p90} />
        {getLatencyStats(data).map((v) => {
          return (
            <Box className={latencyClasses.statContainer}>
              <span className={latencyClasses.statTitle}>{v.name}</span>
              <Text>
                <span className={latencyClasses.statValue}>{v.value}</span>
                <span className={latencyClasses.statUnit}>{v.unit}</span>
              </Text>
            </Box>
          );
        })}
      </React.Fragment>
    );
  }

  return (
    <Box className={latencyClasses.latencyContainer}>
      <Group gap="md" pos="relative">
        <PlotIcon color="#A9A0B2" />
        Segment Latencies
        {innerElement}
      </Group>
    </Box>
  );
});

function getLatencyStats(
  data: SegmentLatencyResponse | null,
): { name: string; value?: number; unit: string }[] {
  return [
    {
      name: "Std Dev",
      value: data?.standardDeviation,
      unit: "ms",
    },
    {
      name: "Avg",
      value: data?.average,
      unit: "ms",
    },
    {
      name: "P50",
      value: data?.median,
      unit: "ms",
    },
    {
      name: "P90",
      value: data?.p90,
      unit: "ms",
    },
    {
      name: "P99",
      value: data?.p99,
      unit: "ms",
    },
  ];
}

interface LatencyPlotProps {
  p50: number;
  p99: number;
}

const LatencyPlot: React.FC<LatencyPlotProps> = ({ p50, p99 }) => {
  let totalDur = 2000;
  if (p99 > 20000) {
    totalDur = 40000;
  } else if (p99 > 8000) {
    totalDur = 20000;
  } else if (p99 > 5000) {
    totalDur = 12000;
  } else if (p99 > 3000) {
    totalDur = 8000;
  } else if (p99 > 2000) {
    totalDur = 5000;
  } else if (p99 > 1000) {
    totalDur = 3000;
  } else if (p99 < 500) {
    totalDur = 1000;
  }
  return (
    <Box
      className={latencyClasses.latencyPlotContainer}
      style={{ "--p50": p50, "--p99": p99, "--total-dur": totalDur }}
    >
      <Box className={latencyClasses.p50PlotBox}>50%</Box>
      <Box className={latencyClasses.p99PlotBox}>90%</Box>
      <Box className={latencyClasses.finalPlotBox}>
        {totalDur / 1000} {totalDur === 1000 ? "second" : "seconds"}
      </Box>
    </Box>
  );
};

export default LatencyDisplay;
